// SPDX-License-Identifier: AGPL-3.0-or-later

import {loadGuildTasks} from '@app/features/tasks/commands/TaskCommands';
import styles from '@app/features/tasks/components/MessageTaskBadge.module.css';
import {STATUS_COLORS, TaskEditDialog} from '@app/features/tasks/components/TaskEditDialog';
import type {TaskStatus} from '@app/features/tasks/state/Tasks';
import Tasks from '@app/features/tasks/state/Tasks';
import {
	DOING_COLUMN_DESCRIPTOR,
	DONE_DESCRIPTOR,
	INPUT_COLUMN_DESCRIPTOR,
	QA_COLUMN_DESCRIPTOR,
	TODO_COLUMN_DESCRIPTOR,
} from '@app/features/tasks/utils/TaskMessageDescriptors';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {CheckSquareIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

const TODO = msg({message: 'To-do', comment: 'Small tag next to the time of a chat message that was made into a to-do.'});

const COLUMNS: Record<TaskStatus, MessageDescriptor> = {
	todo: TODO_COLUMN_DESCRIPTOR,
	input: INPUT_COLUMN_DESCRIPTOR,
	doing: DOING_COLUMN_DESCRIPTOR,
	qa: QA_COLUMN_DESCRIPTOR,
	done: DONE_DESCRIPTOR,
};

/** A quiet "To-do · column" tag next to a message's time when a to-do was made from it. Click opens the to-do. */
export const MessageTaskBadge = observer(function MessageTaskBadge({
	messageId,
	guildId,
}: {
	messageId: string;
	guildId: string | null | undefined;
}) {
	const {i18n} = useLingui();
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (guildId) void loadGuildTasks(guildId);
	}, [guildId]);

	const task = Tasks.getTaskForMessage(messageId);
	if (!task || task.archived_at) return null;
	return (
		<>
			<button
				type="button"
				className={styles.badge}
				title={task.title}
				onClick={(event) => {
					event.stopPropagation();
					setOpen(true);
				}}
				data-flx="tasks.message-task-badge.open.button"
			>
				<CheckSquareIcon size={12} weight="bold" aria-hidden="true" data-flx="tasks.message-task-badge.icon" />
				{i18n._(TODO)}
				<span className={styles.dot} style={{background: STATUS_COLORS[task.status]}} data-flx="tasks.message-task-badge.dot" />
				{i18n._(COLUMNS[task.status])}
			</button>
			{open && <TaskEditDialog task={task} onClose={() => setOpen(false)} data-flx="tasks.message-task-badge.dialog" />}
		</>
	);
});
